// client/src/screens/admin/products/haberdashery/HaberdasheryFormContent.js

import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import Uploader from "../../../../components/images/Uploader";
import CreateSubcategoryModal from "../../../../components/modals/CreateSubcategoryModal";
import CreateCategoryModal from "../../../../components/modals/CreateCategoryModal";

import { adminListCategories } from "../../../../actions/categoryActions";
import { adminListSubcategories } from "../../../../actions/subcategoryActions";

import {
  FLAG_FIELDS,
  HABERDASHERY_UNIT_OPTIONS,
  SHOP_KEY,
  Field,
  Section,
  getDefaultUnitValue,
  getIdValue,
  getUnitMeta,
  isLengthType,
  mergeById,
  normalizeSku,
  normalizeUnitType,
  safeText,
} from "./HaberdasheryForm.shared";

/* -------------------------------------------------------------------------- */
/*                              Catalog section                               */
/* -------------------------------------------------------------------------- */

const HaberdasheryCatalogSection = memo(function HaberdasheryCatalogSection({
  shopKey = SHOP_KEY,
  category,
  subcategory,
  disabled = false,
  onCategoryChange,
  onSubcategoryChange,
}) {
  const dispatch = useDispatch();

  const categoryState = useSelector((state) => state.adminCategoryList || {});
  const subcategoryState = useSelector(
    (state) => state.adminSubcategoryList || {},
  );

  const [createdCategories, setCreatedCategories] = useState([]);
  const [createdSubcategories, setCreatedSubcategories] = useState([]);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);

  const categoryList = useMemo(
    () => categoryState.categories || categoryState.items || [],
    [categoryState.categories, categoryState.items],
  );
  const subcategoryList = useMemo(
    () => subcategoryState.subcategories || subcategoryState.items || [],
    [subcategoryState.items, subcategoryState.subcategories],
  );

  const loadingCategories = Boolean(categoryState.loading);
  const loadingSubcategories = Boolean(subcategoryState.loading);

  const categoriesError = safeText(categoryState.error);
  const subcategoriesError = safeText(subcategoryState.error);

  useEffect(() => {
    if (!shopKey) return;

    dispatch(adminListCategories({ shopKey }));
    dispatch(adminListSubcategories({ shopKey }));
  }, [dispatch, shopKey]);

  useEffect(() => {
    if (!category) onSubcategoryChange("");
  }, [category, onSubcategoryChange]);

  const categories = useMemo(
    () =>
      mergeById([...categoryList, ...createdCategories]).filter((item) => {
        const itemShopKey = safeText(item?.shopKey);
        return !shopKey || !itemShopKey || itemShopKey === shopKey;
      }),
    [categoryList, createdCategories, shopKey],
  );

  const subcategories = useMemo(
    () =>
      mergeById([...subcategoryList, ...createdSubcategories]).filter(
        (item) => {
          const itemShopKey = safeText(item?.shopKey);
          const parentId = getIdValue(item?.parentId);

          const sameShop = !shopKey || !itemShopKey || itemShopKey === shopKey;
          const sameParent = !category || parentId === category;

          return sameShop && sameParent;
        },
      ),
    [category, createdSubcategories, shopKey, subcategoryList],
  );

  const handleCreatedCategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      setCreatedCategories((prev) => mergeById([...prev, created]));
      onCategoryChange(id);
      onSubcategoryChange("");
      setShowCategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  const handleCreatedSubcategory = useCallback(
    (created) => {
      const id = getIdValue(created);
      if (!id) return;

      const parentId = getIdValue(created?.parentId);

      setCreatedSubcategories((prev) => mergeById([...prev, created]));

      if (parentId) onCategoryChange(parentId);
      onSubcategoryChange(id);
      setShowSubcategoryModal(false);
    },
    [onCategoryChange, onSubcategoryChange],
  );

  return (
    <>
      <Section
        title="Category Assignment"
        hint="Choose an existing category or create one without leaving the product form."
      >
        <div className="admin-form-grid admin-form-grid--2">
          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Category</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowCategoryModal(true)}
                disabled={disabled || !shopKey}
              >
                + New category
              </button>
            </div>

            <select
              className="admin-select"
              value={category}
              onChange={(event) => {
                onCategoryChange(event.target.value);
                onSubcategoryChange("");
              }}
              disabled={disabled || !shopKey || loadingCategories}
            >
              <option value="">
                {!shopKey
                  ? "Choose shop first"
                  : loadingCategories
                    ? "Loading categories..."
                    : categories.length
                      ? "Select category"
                      : "No categories yet"}
              </option>

              {categories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {categoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {categoriesError}
              </div>
            ) : null}
          </div>

          <div className="admin-field">
            <div className="admin-field-row">
              <span className="admin-field__label">Subcategory</span>

              <button
                type="button"
                className="admin-btn admin-btn--secondary"
                onClick={() => setShowSubcategoryModal(true)}
                disabled={disabled || !shopKey || !category}
              >
                + New subcategory
              </button>
            </div>

            <select
              className="admin-select"
              value={subcategory}
              onChange={(event) => onSubcategoryChange(event.target.value)}
              disabled={disabled || !category || loadingSubcategories}
            >
              <option value="">
                {!category
                  ? "Choose category first"
                  : loadingSubcategories
                    ? "Loading subcategories..."
                    : subcategories.length
                      ? "Select subcategory"
                      : "No subcategories yet"}
              </option>

              {subcategories.map((item) => {
                const id = getIdValue(item);

                return (
                  <option key={id} value={id}>
                    {item.name}
                  </option>
                );
              })}
            </select>

            {subcategoriesError ? (
              <div className="admin-feedback admin-feedback--error">
                {subcategoriesError}
              </div>
            ) : null}
          </div>
        </div>
      </Section>

      <CreateCategoryModal
        show={showCategoryModal}
        onHide={() => setShowCategoryModal(false)}
        shopKey={shopKey}
        onCreated={handleCreatedCategory}
      />

      <CreateSubcategoryModal
        show={showSubcategoryModal}
        onHide={() => setShowSubcategoryModal(false)}
        shopKey={shopKey}
        parentId={category}
        categories={categories}
        onCreated={handleCreatedSubcategory}
      />
    </>
  );
});

/* -------------------------------------------------------------------------- */
/*                               Basic section                                */
/* -------------------------------------------------------------------------- */

const HaberdasheryBasicSection = memo(function HaberdasheryBasicSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Basic Information"
      hint="Name, description, category, and SKU rules must match backend validation."
    >
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Product name">
          <input
            className="admin-input"
            value={form.name}
            placeholder="Product name"
            disabled={disabled}
            onChange={(event) => setField("name", event.target.value)}
          />
        </Field>

        <Field label="Brand">
          <input
            className="admin-input"
            value={form.brand}
            placeholder="Brand"
            disabled={disabled}
            onChange={(event) => setField("brand", event.target.value)}
          />
        </Field>

        {!form.isVariant ? (
          <Field label="SKU" hint="Required for simple products.">
            <input
              className="admin-input"
              value={form.sku}
              placeholder="SKU"
              disabled={disabled}
              onChange={(event) => setField("sku", event.target.value)}
              onBlur={() => setField("sku", normalizeSku(form.sku))}
            />
          </Field>
        ) : (
          <Field label="Variant mode">
            <input
              className="admin-input"
              value="Each variant has its own SKU"
              disabled
              readOnly
            />
          </Field>
        )}
      </div>

      <div className="admin-form-spacer">
        <Field label="Description">
          <textarea
            className="admin-textarea"
            value={form.description}
            placeholder="Description"
            rows={4}
            disabled={disabled}
            onChange={(event) => setField("description", event.target.value)}
          />
        </Field>
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Selling section                               */
/* -------------------------------------------------------------------------- */

const HaberdasherySimpleSellingSection = memo(
  function HaberdasherySimpleSellingSection({ form, disabled, setField }) {
    const unitMeta = getUnitMeta(form.unitType);
    const lengthBased = isLengthType(form.unitType);

    return (
      <div className="admin-form-grid admin-form-grid--4">
        <Field label="Unit type">
          <select
            className="admin-select"
            value={form.unitType}
            disabled={disabled}
            onChange={(event) => {
              const nextType = normalizeUnitType(event.target.value);

              setField("unitType", nextType);
              setField("unitValue", String(getDefaultUnitValue(nextType)));

              if (isLengthType(nextType)) {
                setField("countInStock", "");
                setField("countReserved", "0");
              } else {
                setField("lengthInStock", "");
                setField("lengthReserved", "0");
              }
            }}
          >
            {HABERDASHERY_UNIT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Unit value" hint="Usually 1. Use 0.5 for half metre.">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.001"
            value={form.unitValue}
            disabled={disabled}
            onChange={(event) => setField("unitValue", event.target.value)}
          />
        </Field>

        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={form.price}
            disabled={disabled}
            onChange={(event) => setField("price", event.target.value)}
          />
        </Field>

        <Field
          label="Unit label"
          hint={`Default: ${unitMeta.label.toLowerCase()}`}
        >
          <input
            className="admin-input"
            value={form.unitLabel}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) => setField("unitLabel", event.target.value)}
          />
        </Field>

        {lengthBased ? (
          <>
            <Field label="Length in stock">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.001"
                value={form.lengthInStock}
                disabled={disabled}
                onChange={(event) =>
                  setField("lengthInStock", event.target.value)
                }
              />
            </Field>

            <Field label="Length reserved">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.001"
                value={form.lengthReserved}
                disabled={disabled}
                onChange={(event) =>
                  setField("lengthReserved", event.target.value)
                }
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Stock">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="1"
                value={form.countInStock}
                disabled={disabled}
                onChange={(event) =>
                  setField("countInStock", event.target.value)
                }
              />
            </Field>

            <Field label="Reserved">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="1"
                value={form.countReserved}
                disabled={disabled}
                onChange={(event) =>
                  setField("countReserved", event.target.value)
                }
              />
            </Field>

            <Field label="Items per pack">
              <input
                className="admin-input"
                type="number"
                min="1"
                step="1"
                value={form.itemsPerPack}
                disabled={disabled}
                onChange={(event) =>
                  setField("itemsPerPack", event.target.value)
                }
              />
            </Field>
          </>
        )}
      </div>
    );
  },
);

const HaberdasheryVariantRow = memo(function HaberdasheryVariantRow({
  variant,
  index,
  disabled,
  setVariantField,
  removeVariant,
}) {
  const unitMeta = getUnitMeta(variant.variantType);
  const lengthBased = isLengthType(variant.variantType);

  const setVariantImages = useCallback(
    (value) => {
      setVariantField(index, "images", value);
    },
    [index, setVariantField],
  );

  return (
    <div className="admin-variant-card">
      <div className="admin-form-grid admin-form-grid--4">
        <Field label="Variant name">
          <input
            className="admin-input"
            value={variant.name}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "name", event.target.value)
            }
          />
        </Field>

        <Field label="SKU">
          <input
            className="admin-input"
            value={variant.sku}
            placeholder="SKU"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "sku", event.target.value)
            }
            onBlur={() =>
              setVariantField(index, "sku", normalizeSku(variant.sku))
            }
          />
        </Field>

        <Field label="Variant type">
          <select
            className="admin-select"
            value={variant.variantType}
            disabled={disabled}
            onChange={(event) => {
              const nextType = normalizeUnitType(event.target.value);

              setVariantField(index, "variantType", nextType);
              setVariantField(
                index,
                "unitValue",
                String(getDefaultUnitValue(nextType)),
              );

              if (isLengthType(nextType)) {
                setVariantField(index, "countInStock", "");
                setVariantField(index, "countReserved", "0");
                setVariantField(index, "itemsPerPack", "1");
              } else {
                setVariantField(index, "lengthInStock", "");
                setVariantField(index, "lengthReserved", "0");
              }
            }}
          >
            {HABERDASHERY_UNIT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Price">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={variant.price}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "price", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--4 admin-form-spacer">
        <Field
          label="Unit value"
          hint={`Default for ${unitMeta.label.toLowerCase()}`}
        >
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.001"
            value={variant.unitValue}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "unitValue", event.target.value)
            }
          />
        </Field>

        <Field label="Unit label">
          <input
            className="admin-input"
            value={variant.unitLabel}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "unitLabel", event.target.value)
            }
          />
        </Field>

        {lengthBased ? (
          <>
            <Field label="Length in stock">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.001"
                value={variant.lengthInStock}
                disabled={disabled}
                onChange={(event) =>
                  setVariantField(index, "lengthInStock", event.target.value)
                }
              />
            </Field>

            <Field label="Length reserved">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="0.001"
                value={variant.lengthReserved}
                disabled={disabled}
                onChange={(event) =>
                  setVariantField(index, "lengthReserved", event.target.value)
                }
              />
            </Field>
          </>
        ) : (
          <>
            <Field label="Stock">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="1"
                value={variant.countInStock}
                disabled={disabled}
                onChange={(event) =>
                  setVariantField(index, "countInStock", event.target.value)
                }
              />
            </Field>

            <Field label="Reserved">
              <input
                className="admin-input"
                type="number"
                min="0"
                step="1"
                value={variant.countReserved}
                disabled={disabled}
                onChange={(event) =>
                  setVariantField(index, "countReserved", event.target.value)
                }
              />
            </Field>

            <Field label="Items per pack">
              <input
                className="admin-input"
                type="number"
                min="1"
                step="1"
                value={variant.itemsPerPack}
                disabled={disabled}
                onChange={(event) =>
                  setVariantField(index, "itemsPerPack", event.target.value)
                }
              />
            </Field>
          </>
        )}
      </div>

      <div className="admin-form-grid admin-form-grid--4 admin-form-spacer">
        <Field label="Color">
          <input
            className="admin-input"
            value={variant.color}
            placeholder="Color"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "color", event.target.value)
            }
          />
        </Field>

        <Field label="Size">
          <input
            className="admin-input"
            value={variant.size}
            placeholder="Size"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "size", event.target.value)
            }
          />
        </Field>

        <Field label="Material">
          <input
            className="admin-input"
            value={variant.material}
            placeholder="Material"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "material", event.target.value)
            }
          />
        </Field>

        <Field label="Length / measurement">
          <input
            className="admin-input"
            value={variant.length}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "length", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field
          label="Short variant description"
          hint="Optional. Describe only what is distinctive about this variant."
        >
          <textarea
            className="admin-textarea"
            value={variant.shortDescription}
            placeholder="Optional"
            maxLength={240}
            rows={3}
            disabled={disabled}
            onChange={(event) =>
              setVariantField(index, "shortDescription", event.target.value)
            }
          />
        </Field>
      </div>

      <div className="admin-field admin-form-spacer">
        <span className="admin-field__label">Variant images</span>

        <Uploader
          existingImages={variant.images || []}
          setImageUrls={setVariantImages}
          folder="products"
          disabled={disabled}
        />

        <span className="admin-field__hint">
          Variant images override product-level imagery where appropriate.
        </span>
      </div>

      <div className="admin-form-actions">
        <button
          type="button"
          className="admin-btn admin-btn--secondary"
          onClick={() => removeVariant(index)}
          disabled={disabled}
        >
          Remove variant
        </button>
      </div>
    </div>
  );
});

const HaberdasherySellingSection = memo(function HaberdasherySellingSection({
  form,
  disabled,
  setField,
  setVariantField,
  addVariant,
  removeVariant,
}) {
  return (
    <Section
      title="Selling Setup"
      hint="Simple products use one root SKU. Variant products require one SKU per variant."
    >
      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <label className="admin-inline-check">
          <input
            type="radio"
            name="haberdashery-selling-mode"
            checked={!form.isVariant}
            disabled={disabled}
            onChange={() => setField("isVariant", false)}
          />
          <span>Single product</span>
        </label>

        <label className="admin-inline-check">
          <input
            type="radio"
            name="haberdashery-selling-mode"
            checked={form.isVariant}
            disabled={disabled}
            onChange={() => setField("isVariant", true)}
          />
          <span>Product with variants</span>
        </label>
      </div>

      <div className="admin-form-grid admin-form-grid--3 admin-form-spacer">
        <Field label="Pricing unit" hint='e.g. "each" or "per garment".'>
          <input
            className="admin-input"
            value={form.pricingUnit}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) => setField("pricingUnit", event.target.value)}
          />
        </Field>

        <Field label="Included quantity" hint="For service bundles only.">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="1"
            value={form.includedQuantity}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setField("includedQuantity", event.target.value)
            }
          />
        </Field>

        <Field label="Included item label" hint="e.g. buttonholes or eyelets.">
          <input
            className="admin-input"
            value={form.includedItemLabel}
            placeholder="Optional"
            disabled={disabled}
            onChange={(event) =>
              setField("includedItemLabel", event.target.value)
            }
          />
        </Field>
      </div>

      {!form.isVariant ? (
        <HaberdasherySimpleSellingSection
          form={form}
          disabled={disabled}
          setField={setField}
        />
      ) : (
        <>
          <p className="admin-form-section__hint admin-form-spacer">
            Each variant needs its own SKU, price, stock model, and optional
            distinguishing attributes.
          </p>

          {form.variants.map((variant, index) => (
            <HaberdasheryVariantRow
              key={variant._clientId || `haberdashery-variant-${index}`}
              variant={variant}
              index={index}
              disabled={disabled}
              setVariantField={setVariantField}
              removeVariant={removeVariant}
            />
          ))}

          <div className="admin-form-actions">
            <button
              type="button"
              className="admin-btn admin-btn--secondary"
              onClick={addVariant}
              disabled={disabled}
            >
              Add variant
            </button>
          </div>
        </>
      )}
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Details section                               */
/* -------------------------------------------------------------------------- */

const HaberdasheryDetailsSection = memo(function HaberdasheryDetailsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Haberdashery Details"
      hint="These fields power filtering, product discovery, and public merchandising."
    >
      <div className="admin-form-grid admin-form-grid--3">
        <Field label="Finish">
          <input
            className="admin-input"
            value={form.finish}
            placeholder="Finish"
            disabled={disabled}
            onChange={(event) => setField("finish", event.target.value)}
          />
        </Field>

        <Field label="Width (mm)">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={form.widthMm}
            placeholder="Width in mm"
            disabled={disabled}
            onChange={(event) => setField("widthMm", event.target.value)}
          />
        </Field>

        <Field label="Diameter (mm)">
          <input
            className="admin-input"
            type="number"
            min="0"
            step="0.01"
            value={form.diameterMm}
            placeholder="Diameter in mm"
            disabled={disabled}
            onChange={(event) => setField("diameterMm", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field label="Compatibility">
          <input
            className="admin-input"
            value={form.compatibility}
            placeholder="e.g. coat, denim, upholstery"
            disabled={disabled}
            onChange={(event) => setField("compatibility", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-spacer">
        <Field label="Compatibilities" hint="Comma or new-line separated.">
          <textarea
            className="admin-textarea"
            value={form.compatibilitiesText}
            rows={2}
            disabled={disabled}
            placeholder="coat, jacket, upholstery"
            onChange={(event) =>
              setField("compatibilitiesText", event.target.value)
            }
          />
        </Field>
      </div>

      {!form.isVariant ? (
        <div className="admin-form-grid admin-form-grid--3 admin-form-spacer">
          <Field label="Colors" hint="Comma or new-line separated.">
            <textarea
              className="admin-textarea"
              value={form.colorsText}
              rows={3}
              disabled={disabled}
              placeholder="Black, Brass"
              onChange={(event) => setField("colorsText", event.target.value)}
            />
          </Field>

          <Field label="Sizes" hint="Comma or new-line separated.">
            <textarea
              className="admin-textarea"
              value={form.sizesText}
              rows={3}
              disabled={disabled}
              placeholder="Small, Large"
              onChange={(event) => setField("sizesText", event.target.value)}
            />
          </Field>

          <Field label="Materials" hint="Comma or new-line separated.">
            <textarea
              className="admin-textarea"
              value={form.materialsText}
              rows={3}
              disabled={disabled}
              placeholder="Cotton, Metal"
              onChange={(event) =>
                setField("materialsText", event.target.value)
              }
            />
          </Field>
        </div>
      ) : null}

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Patterns">
          <textarea
            className="admin-textarea"
            value={form.patternsText}
            rows={2}
            disabled={disabled}
            placeholder="Floral, Plain"
            onChange={(event) => setField("patternsText", event.target.value)}
          />
        </Field>

        <Field label="Tags">
          <textarea
            className="admin-textarea"
            value={form.tagsText}
            rows={2}
            disabled={disabled}
            placeholder="buttons, trims"
            onChange={(event) => setField("tagsText", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Groups">
          <textarea
            className="admin-textarea"
            value={form.groupsText}
            rows={2}
            disabled={disabled}
            placeholder="new season, archival"
            onChange={(event) => setField("groupsText", event.target.value)}
          />
        </Field>

        <Field label="Origin">
          <input
            className="admin-input"
            value={form.origin}
            placeholder="Origin"
            disabled={disabled}
            onChange={(event) => setField("origin", event.target.value)}
          />
        </Field>
      </div>

      <div className="admin-form-grid admin-form-grid--2 admin-form-spacer">
        <Field label="Usage">
          <input
            className="admin-input"
            value={form.usage}
            placeholder="Usage"
            disabled={disabled}
            onChange={(event) => setField("usage", event.target.value)}
          />
        </Field>

        <Field label="Care instructions">
          <textarea
            className="admin-textarea"
            value={form.careInstructions}
            rows={3}
            disabled={disabled}
            placeholder="Care instructions"
            onChange={(event) =>
              setField("careInstructions", event.target.value)
            }
          />
        </Field>
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                              Images / flags                                */
/* -------------------------------------------------------------------------- */

const HaberdasheryImagesSection = memo(function HaberdasheryImagesSection({
  images,
  disabled,
  setImages,
}) {
  return (
    <Section
      title="Product Images"
      hint="The first image is used as the cover image."
    >
      <Uploader
        existingImages={images}
        setImageUrls={setImages}
        folder="products"
        disabled={disabled}
      />
    </Section>
  );
});

const HaberdasheryMadeToMeasureSection = memo(
  function HaberdasheryMadeToMeasureSection({ form, disabled, setField }) {
    return (
      <Section
        title="Made To Measure"
        hint="Only staff-approved trims appear in the guided made-to-measure finishing step."
      >
        <label className="admin-inline-check">
          <input
            type="checkbox"
            checked={Boolean(form.madeToMeasureEligible)}
            disabled={disabled}
            onChange={(event) =>
              setField("madeToMeasureEligible", event.target.checked)
            }
          />
          <span>Available as a made-to-measure finishing option</span>
        </label>

        <div className="admin-form-spacer">
          <Field label="Suitability notes">
            <textarea
              className="admin-textarea"
              value={form.madeToMeasureSuitabilityNotes}
              rows={3}
              disabled={disabled}
              placeholder="Compatibility, finish, or construction notes for MTM."
              onChange={(event) =>
                setField("madeToMeasureSuitabilityNotes", event.target.value)
              }
            />
          </Field>
        </div>
      </Section>
    );
  },
);

const HaberdasheryFlagsSection = memo(function HaberdasheryFlagsSection({
  form,
  disabled,
  setField,
}) {
  return (
    <Section
      title="Visibility & Flags"
      hint="These content flags are editable on create and edit. Publishing and draft state are handled from the product list."
    >
      <div className="admin-flags-grid">
        {FLAG_FIELDS.map((key) => (
          <label key={key} className="admin-inline-check">
            <input
              type="checkbox"
              checked={Boolean(form[key])}
              disabled={disabled}
              onChange={(event) => setField(key, event.target.checked)}
            />
            <span>{key}</span>
          </label>
        ))}
      </div>
    </Section>
  );
});

/* -------------------------------------------------------------------------- */
/*                                  Content                                   */
/* -------------------------------------------------------------------------- */

const HaberdasheryFormContent = memo(function HaberdasheryFormContent({
  form,
  shopKey = SHOP_KEY,
  submitDisabled = false,
  setField,
  setVariantField,
  setImages,
  addVariant,
  removeVariant,
}) {
  return (
    <>
      <HaberdasheryCatalogSection
        shopKey={shopKey || SHOP_KEY}
        category={form.category}
        subcategory={form.subcategory}
        disabled={submitDisabled}
        onCategoryChange={(value) => setField("category", value)}
        onSubcategoryChange={(value) => setField("subcategory", value)}
      />

      <HaberdasheryBasicSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <HaberdasherySellingSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
        setVariantField={setVariantField}
        addVariant={addVariant}
        removeVariant={removeVariant}
      />

      <HaberdasheryDetailsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <HaberdasheryImagesSection
        images={form.images}
        disabled={submitDisabled}
        setImages={setImages}
      />

      <HaberdasheryMadeToMeasureSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />

      <HaberdasheryFlagsSection
        form={form}
        disabled={submitDisabled}
        setField={setField}
      />
    </>
  );
});

export default HaberdasheryFormContent;
